import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { logout, getOrganizerCounts } from '../../services/api';

/**
 * DashboardSidebar - Menú lateral para el panel del organizador
 */
const DashboardSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');
    const [isEventosOpen, setIsEventosOpen] = React.useState(false);

    const { data: counts } = useQuery({
        queryKey: ['organizer-counts', userId],
        queryFn: () => getOrganizerCounts(userId),
        enabled: !!userId
    });

    const menuItems = [
        { label: 'Panel Principal', icon: '📊', path: '/dashboard' },
        { 
            label: 'Mis Eventos', 
            icon: '🎾', 
            hasSubmenu: true,
            subItems: [
                { label: 'Pozos', icon: '🎾', path: '/dashboard/events', badge: counts?.events },
                { label: 'Torneos', icon: '🏆', comingSoon: true },
                { label: 'Ligas', icon: '⚔️', comingSoon: true },
                { label: 'Ranking', icon: '📊', comingSoon: true },
                { label: 'Partidos', icon: '🎯', comingSoon: true },
            ]
        },
        { label: 'Mis Clubes', icon: '🏢', path: '/dashboard/clubs', badge: counts?.clubs },
        { label: 'Jugadores', icon: '👥', path: '/dashboard/players', badge: counts?.players },
        { label: 'Finanzas', icon: '💰', path: '/dashboard/finance' },
        { label: 'Configuración', icon: '⚙️', path: '/dashboard/settings' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleEventos = () => {
        setIsEventosOpen(!isEventosOpen);
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed md:sticky top-0 bottom-0 left-0 z-50
                w-64 bg-[#0f172a] text-slate-400
                min-h-screen flex flex-col border-r border-slate-800
                transition-all duration-300 ease-in-out
                ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                {/* Header del Sidebar */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-center h-20">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">🎾</span>
                            <span className="text-lg font-black tracking-tight text-white">VISIO<span className="text-primary-500">PADEL</span></span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest pl-1">Panel de Organizador</p>
                    </div>
                    {/* Mobile Close Button */}
                    <button
                        onClick={onClose}
                        className="md:hidden text-slate-500 hover:text-white"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Menu Nav */}
                <nav className="flex-grow p-4 mt-4 space-y-2 overflow-y-auto">
                    {menuItems.map((item) => (
                        <div key={item.label}>
                            {item.hasSubmenu ? (
                                <>
                                    {/* Menú con Submenu */}
                                    <button
                                        onClick={toggleEventos}
                                        className={`
                                            flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-semibold w-full text-sm
                                            ${isEventosOpen
                                                ? 'bg-slate-800 text-white'
                                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                                        `}
                                    >
                                        <span className="text-lg">{item.icon}</span>
                                        <span className="flex-grow text-left">{item.label}</span>
                                        <svg 
                                            className={`w-4 h-4 transition-transform text-slate-500 ${isEventosOpen ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            viewBox="0 0 24 24" 
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {/* Submenú */}
                                    {isEventosOpen && (
                                        <div className="ml-4 mt-2 space-y-1">
                                            {item.subItems.map((subItem) => (
                                                subItem.comingSoon ? (
                                                    <div
                                                        key={subItem.label}
                                                        className="flex items-center gap-3 px-4 py-2 rounded-lg text-slate-600 cursor-not-allowed opacity-60"
                                                    >
                                                        <span className="text-lg">{subItem.icon}</span>
                                                        <span className="flex-grow text-sm">{subItem.label}</span>
                                                        <span className="text-[8px] bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                                                            Póximamente
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <NavLink
                                                        key={subItem.path}
                                                        to={subItem.path}
                                                        onClick={() => onClose && onClose()}
                                                        className={({ isActive }) => `
                                                            flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all font-medium text-xs sm:text-sm
                                                            ${isActive
                                                                ? 'bg-primary-500/10 text-primary-400'
                                                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                                                        `}
                                                    >
                                                        <span className="text-base">{subItem.icon}</span>
                                                        <span className="flex-grow">{subItem.label}</span>
                                                        {subItem.badge > 0 && (
                                                            <span className="bg-primary-500/20 text-primary-400 text-[10px] font-black px-1.5 py-0.5 rounded-md min-w-[20px] text-center">
                                                                {subItem.badge}
                                                            </span>
                                                        )}
                                                    </NavLink>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Menú Normal sin Submenu */
                                <NavLink
                                    to={item.path}
                                    end={item.path === '/dashboard'}
                                    onClick={() => onClose && onClose()}
                                    className={({ isActive }) => `
                                        flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-semibold text-sm
                                        ${isActive
                                            ? 'bg-primary-500/10 text-primary-400 shadow-sm'
                                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                                    `}
                                >
                                    <span className="text-lg">{item.icon}</span>
                                    <span className="flex-grow">{item.label}</span>
                                    {item.badge > 0 && (
                                        <span className="bg-primary-500/20 text-primary-400 text-[10px] font-black px-1.5 py-0.5 rounded-md min-w-[20px] text-center">
                                            {item.badge}
                                        </span>
                                    )}
                                </NavLink>
                            )}
                        </div>
                    ))}
                </nav>

                {/* Footer del Sidebar */}
                <div className="p-6 border-t border-slate-800 space-y-4">
                    <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-800">
                        <p className="text-xs text-slate-500 mb-1">Plan Actual</p>
                        <p className="text-sm font-bold text-white">Premium Pro ⭐</p>
                    </div>

                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-red-400/80 hover:bg-red-500/10 hover:text-red-400 w-full text-sm"
                    >
                        <span className="text-xl">🚪</span>
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default DashboardSidebar;
